import type { EvidenceAdapterDescriptor } from './types.js';

const LOCAL_NETWORK = Object.freeze({
  requiresNetwork: false,
  dataDestination: 'local-runtime',
  mediaTransmission: 'NONE' as const,
  privacyClass: 'LOCAL_ONLY' as const,
  credentialRequirement: 'NONE' as const,
});

export const C2PA_ADAPTER_DESCRIPTOR = Object.freeze({
  id: 'c2pa',
  version: '1.0.0',
  category: 'STANDARD',
  capabilities: ['C2PA_CONTENT_CREDENTIALS', 'C2PA_TRUST', 'C2PA_ASSET_BINDING'],
  evidence_methods: ['CRYPTOGRAPHIC_PROVENANCE'],
  availability: 'AVAILABLE',
  execution: ['BROWSER', 'SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: LOCAL_NETWORK,
} satisfies EvidenceAdapterDescriptor);

export const METADATA_ADAPTER_DESCRIPTOR = Object.freeze({
  id: 'metadata',
  version: '1.0.0',
  category: 'METADATA',
  capabilities: ['EXIF_SAFE_SUBSET', 'XMP_SAFE_SUBSET', 'IPTC_SAFE_SUBSET'],
  evidence_methods: ['EMBEDDED_METADATA'],
  availability: 'AVAILABLE',
  execution: ['BROWSER', 'SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: LOCAL_NETWORK,
} satisfies EvidenceAdapterDescriptor);

export const C2PA_OFFICIAL_TRUST_LIST_DESCRIPTOR = Object.freeze({
  id: 'c2pa-official-trust-list',
  version: '1.0.0',
  category: 'TRUST',
  capabilities: ['C2PA_OFFICIAL_TRUST_LIST', 'C2PA_SIGNER_TRUST'],
  evidence_methods: ['TRUST_REGISTRY'],
  availability: 'AVAILABLE',
  execution: ['SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: {
    requiresNetwork: true,
    dataDestination: 'Official C2PA conformance trust-list repository',
    mediaTransmission: 'NONE',
    privacyClass: 'THIRD_PARTY',
    credentialRequirement: 'NONE',
  },
} satisfies EvidenceAdapterDescriptor);

export const C2PA_OFFICIAL_TSA_TRUST_LIST_DESCRIPTOR = Object.freeze({
  id: 'c2pa-tsa-trust-list',
  version: '1.0.0',
  category: 'TRUST',
  capabilities: ['C2PA_OFFICIAL_TSA_TRUST_LIST', 'C2PA_TIMESTAMP_TRUST'],
  evidence_methods: ['TIMESTAMP_TRUST'],
  availability: 'AVAILABLE',
  execution: ['SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: {
    requiresNetwork: true,
    dataDestination: 'Official C2PA conformance trust-list repository',
    mediaTransmission: 'NONE',
    privacyClass: 'THIRD_PARTY',
    credentialRequirement: 'NONE',
  },
} satisfies EvidenceAdapterDescriptor);

export const OPENAI_PROVENANCE_DESCRIPTOR = Object.freeze({
  id: 'openai-provenance',
  version: 'descriptor-1.0.0',
  category: 'PROVIDER',
  capabilities: ['OPENAI_C2PA_DETECTION', 'OPENAI_SYNTHID_DETECTION'],
  evidence_methods: ['CRYPTOGRAPHIC_PROVENANCE', 'INVISIBLE_WATERMARK'],
  availability: 'NOT_CONFIGURED',
  execution: ['SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: {
    requiresNetwork: true,
    dataDestination: 'OpenAI Content Provenance API',
    mediaTransmission: 'ARTIFACT',
    privacyClass: 'THIRD_PARTY',
    credentialRequirement: 'REQUIRED',
  },
} satisfies EvidenceAdapterDescriptor);

export const GOOGLE_AI_CONTENT_DETECTION_DESCRIPTOR = Object.freeze({
  id: 'google-ai-content-detection',
  version: 'descriptor-1.0.0',
  category: 'PROVIDER',
  capabilities: ['AI_CONTENT_DETECTION'],
  evidence_methods: ['PROBABILISTIC_DETECTION'],
  availability: 'RESTRICTED',
  execution: ['SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: {
    requiresNetwork: true,
    dataDestination: 'Google Gemini Enterprise Agent Platform',
    mediaTransmission: 'ARTIFACT',
    privacyClass: 'THIRD_PARTY',
    credentialRequirement: 'REQUIRED',
  },
} satisfies EvidenceAdapterDescriptor);

export const GOOGLE_SYNTHID_DIRECT_DETECTION_DESCRIPTOR = Object.freeze({
  id: 'google-synthid-direct-detection',
  version: 'descriptor-1.0.0',
  category: 'WATERMARK',
  capabilities: ['SYNTHID_DIRECT_DETECTION'],
  evidence_methods: ['INVISIBLE_WATERMARK'],
  availability: 'RESTRICTED',
  execution: ['SERVER'],
  media_types: ['image/jpeg', 'image/png'],
  network: {
    requiresNetwork: true,
    dataDestination: 'Google DeepMind SynthID Detector',
    mediaTransmission: 'ARTIFACT',
    privacyClass: 'THIRD_PARTY',
    credentialRequirement: 'REQUIRED',
  },
} satisfies EvidenceAdapterDescriptor);

/** @deprecated R4 compatibility alias. Prefer the explicit direct-detection descriptor. */
export const GOOGLE_SYNTHID_DESCRIPTOR = GOOGLE_SYNTHID_DIRECT_DETECTION_DESCRIPTOR;

export const NON_ACTIVE_ADAPTER_DESCRIPTORS = Object.freeze([
  OPENAI_PROVENANCE_DESCRIPTOR,
  GOOGLE_AI_CONTENT_DETECTION_DESCRIPTOR,
  GOOGLE_SYNTHID_DIRECT_DETECTION_DESCRIPTOR,
] as const);
